
import { useState } from 'react'
import { Search, Phone, Mail, Clock, Calendar, MessageSquare, Users, Lock, ShieldAlert, CheckCircle2 } from 'lucide-react'
import { mockAbroadLeads, mockPartnerLeads } from '@/data/admin-mock-data'
import { useAuth } from '@/context/auth-context'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'framer-motion'

const ADMIN_PASSWORD = 'admin123' // In production, this would be server-side validated

export function CounsellorLeadsView() {
    const { user } = useAuth()

    // Combine leads and filter by assigned mentor (user)
    const myAbroadLeads = mockAbroadLeads.filter(l => l.assignedMentorId === user?.id)
    const myPartnerLeads = mockPartnerLeads.filter(l => l.assignedMentorId === user?.id)

    // Normalize data for display
    const allMyLeads = [
        ...myAbroadLeads.map(l => ({ ...l, type: 'Abroad', source: l.collegeName, interest: l.message })),
        ...myPartnerLeads.map(l => ({ ...l, type: 'Partner', source: l.instituteName, interest: l.courseInterest }))
    ]

    const [leads, setLeads] = useState(allMyLeads)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [selectedLead, setSelectedLead] = useState<any | null>(null)
    const [leadRemark, setLeadRemark] = useState('')
    const [localRemarks, setLocalRemarks] = useState<{ text: string, date: Date, type: 'user' | 'system' }[]>([])
    const [isEditingInterest, setIsEditingInterest] = useState(false)
    const [editedInterest, setEditedInterest] = useState('')

    // Admitted status lock state
    const [showAdmittedPopup, setShowAdmittedPopup] = useState(false)
    const [pendingAdmitLeadId, setPendingAdmitLeadId] = useState<string | null>(null)
    const [lockedLeads, setLockedLeads] = useState<Set<string>>(new Set())
    const [showUnlockModal, setShowUnlockModal] = useState(false)
    const [unlockPassword, setUnlockPassword] = useState('')
    const [unlockLeadId, setUnlockLeadId] = useState<string | null>(null)
    const [passwordError, setPasswordError] = useState(false)

    const filtered = leads.filter(l => {
        const matchSearch = !searchTerm || l.studentName.toLowerCase().includes(searchTerm.toLowerCase()) || l.phone.includes(searchTerm)
        const matchStatus = statusFilter === 'all' || l.status === statusFilter
        return matchSearch && matchStatus
    })

    const statusColors: Record<string, string> = {
        'new': 'bg-blue-100 text-blue-700 border-blue-200',
        'contacted': 'bg-yellow-100 text-yellow-700 border-yellow-200',
        'counselling': 'bg-purple-100 text-purple-700 border-purple-200',
        'applied': 'bg-indigo-100 text-indigo-700 border-indigo-200',
        'admitted': 'bg-green-100 text-green-700 border-green-200',
    }

    const handleUpdateStatus = (leadId: string, newStatus: string) => {
        // If trying to change a locked (admitted) lead, show unlock modal
        if (lockedLeads.has(leadId)) {
            setUnlockLeadId(leadId)
            setUnlockPassword('')
            setPasswordError(false)
            setShowUnlockModal(true)
            return
        }

        // If setting to admitted, show confirmation popup
        if (newStatus === 'admitted') {
            setPendingAdmitLeadId(leadId)
            setShowAdmittedPopup(true)
            return
        }

        // Normal status update
        setLeads((prev: any[]) => prev.map(l => l.id === leadId ? { ...l, status: newStatus as any } : l))
        toast.success(`Lead status updated to ${newStatus}`)
        if (selectedLead && selectedLead.id === leadId) {
            setSelectedLead((prev: any) => prev ? { ...prev, status: newStatus } : null)
        }
    }

    const confirmAdmission = () => {
        if (!pendingAdmitLeadId) return
        setLeads((prev: any[]) => prev.map(l => l.id === pendingAdmitLeadId ? { ...l, status: 'admitted' } : l))
        setLockedLeads(prev => new Set(prev).add(pendingAdmitLeadId))
        toast.success('🎉 Lead marked as Admitted / Enrolled!')
        if (selectedLead && selectedLead.id === pendingAdmitLeadId) {
            setSelectedLead((prev: any) => prev ? { ...prev, status: 'admitted' } : null)
        }
        setShowAdmittedPopup(false)
        setPendingAdmitLeadId(null)
    }

    const handleUnlock = () => {
        if (unlockPassword === ADMIN_PASSWORD) {
            if (unlockLeadId) {
                setLockedLeads(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(unlockLeadId)
                    return newSet
                })
                toast.success('Lead unlocked. You can now change the status.')
            }
            setShowUnlockModal(false)
            setUnlockPassword('')
            setUnlockLeadId(null)
            setPasswordError(false)
        } else {
            setPasswordError(true)
            toast.error('Incorrect password!')
        }
    }

    const handleAddRemark = () => {
        if (!selectedLead || !leadRemark.trim()) return
        const newRemark = { text: leadRemark, date: new Date(), type: 'user' as const }
        setLocalRemarks(prev => [newRemark, ...prev])
        toast.success('Remark added successfully')
        setLeadRemark('')
    }

    const handleSaveInterest = () => {
        if (selectedLead) {
            setSelectedLead((prev: any) => ({ ...prev, interest: editedInterest }))
            setLeads((prev: any[]) => prev.map(l => l.id === selectedLead.id ? { ...l, interest: editedInterest } : l))
            setIsEditingInterest(false)
            toast.success('Interest updated')
        }
    }

    const isLocked = (leadId: string) => lockedLeads.has(leadId)

    // Status options for selects (no 'closed')
    const statusOptions = ['new', 'contacted', 'counselling', 'applied', 'admitted'] as const

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div>
                <h2 className="text-2xl font-bold text-theme-primary">My Assigned Leads</h2>
                <p className="text-sm text-theme-secondary">Manage your leads and track their progress.</p>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <GlassmorphicCard className="p-4">
                    <p className="text-xs font-medium text-theme-muted uppercase">Total Assigned</p>
                    <p className="text-2xl font-bold text-theme-primary mt-1">{leads.length}</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <p className="text-xs font-medium text-theme-muted uppercase">Pending Action</p>
                    <p className="text-2xl font-bold text-yellow-600 mt-1">{leads.filter(l => ['new', 'contacted'].includes(l.status)).length}</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <p className="text-xs font-medium text-theme-muted uppercase">In Progress</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">{leads.filter(l => ['counselling', 'applied'].includes(l.status)).length}</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-1.5">
                        <p className="text-xs font-medium text-theme-muted uppercase">Admitted</p>
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-green-600 mt-1">{leads.filter(l => l.status === 'admitted').length}</p>
                </GlassmorphicCard>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-secondary" />
                    <input
                        type="text"
                        placeholder="Search by student, phone..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary"
                >
                    <option value="all">All statuses</option>
                    {statusOptions.map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>

            {/* Desktop Table View */}
            <GlassmorphicCard className="hidden md:block overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                <th className="p-4 text-sm font-semibold text-theme-primary">Student</th>
                                <th className="p-4 text-sm font-semibold text-theme-primary">Type / Source</th>
                                <th className="p-4 text-sm font-semibold text-theme-primary">Status</th>
                                <th className="p-4 text-sm font-semibold text-theme-primary">Last Update</th>
                                <th className="p-4 text-sm font-semibold text-theme-primary">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length > 0 ? (
                                filtered.map((l) => (
                                    <tr key={l.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                        <td className="p-4">
                                            <p className="font-medium text-theme-primary">{l.studentName}</p>
                                            <div className="flex flex-col text-xs text-theme-muted mt-1">
                                                <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {l.email}</span>
                                                <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {l.phone}</span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase mb-1 ${l.type === 'Abroad' ? 'bg-indigo-100 text-indigo-700' : 'bg-orange-100 text-orange-700'}`}>
                                                {l.type}
                                            </span>
                                            <p className="text-sm font-medium text-theme-primary">{l.source}</p>
                                            {l.interest && <p className="text-xs text-theme-secondary truncate max-w-[200px]" title={l.interest}>{l.interest}</p>}
                                        </td>
                                        <td className="p-4">
                                            {isLocked(l.id) ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                        <Lock className="h-3.5 w-3.5" /> Admitted
                                                    </span>
                                                    <button
                                                        onClick={() => handleUpdateStatus(l.id, '')}
                                                        className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                                        title="Unlock with password"
                                                    >
                                                        <Lock className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <select
                                                    value={l.status}
                                                    onChange={e => handleUpdateStatus(l.id, e.target.value)}
                                                    className={`text-sm font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${statusColors[l.status] || 'bg-slate-100 text-slate-700'}`}
                                                >
                                                    {statusOptions.map(s => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </td>
                                        <td className="p-4 text-sm text-theme-secondary">
                                            <div className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {new Date(l.createdAt).toLocaleDateString()}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <button
                                                onClick={() => {
                                                    setSelectedLead(l)
                                                    setLocalRemarks([
                                                        { text: 'Lead assigned to you.', date: new Date(l.createdAt), type: 'system' }
                                                    ])
                                                    setEditedInterest(l.interest || '')
                                                    setIsEditingInterest(false)
                                                }}
                                                className="px-3 py-1.5 bg-primary-blue/10 text-primary-blue hover:bg-primary-blue/20 rounded-lg text-sm font-medium transition-colors"
                                            >
                                                Manage
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-theme-secondary">
                                        No leads found matching your criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </GlassmorphicCard>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
                {filtered.map((l) => (
                    <GlassmorphicCard key={l.id} className="p-4">
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <h3 className="font-semibold text-theme-primary">{l.studentName}</h3>
                                <p className="text-xs text-theme-secondary">{l.source}</p>
                            </div>
                            {isLocked(l.id) ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                    <Lock className="h-3 w-3" /> Admitted
                                </span>
                            ) : (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[l.status] || 'bg-slate-100'}`}>
                                    {l.status}
                                </span>
                            )}
                        </div>

                        <div className="space-y-3 mb-4">
                            <div className="text-sm flex flex-col gap-1">
                                <span className="flex items-center gap-2 text-theme-secondary"><Phone className="h-3 w-3" /> {l.phone}</span>
                                <span className="flex items-center gap-2 text-theme-secondary"><Mail className="h-3 w-3" /> {l.email}</span>
                            </div>

                            <div>
                                <p className="text-theme-muted text-xs mb-1">Status Action</p>
                                {isLocked(l.id) ? (
                                    <button
                                        onClick={() => handleUpdateStatus(l.id, '')}
                                        className="w-full flex items-center justify-center gap-2 text-sm p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-medium"
                                    >
                                        <Lock className="h-4 w-4" /> Admitted — Locked (Tap to unlock)
                                    </button>
                                ) : (
                                    <select
                                        value={l.status}
                                        onChange={e => handleUpdateStatus(l.id, e.target.value)}
                                        className={`w-full text-sm p-2 rounded-lg border border-slate-200 dark:border-slate-700 ${statusColors[l.status]}`}
                                    >
                                        {statusOptions.map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={() => {
                                setSelectedLead(l)
                                setLocalRemarks([
                                    { text: 'Lead assigned to you.', date: new Date(l.createdAt), type: 'system' }
                                ])
                                setEditedInterest(l.interest || '')
                                setIsEditingInterest(false)
                            }}
                            className="w-full py-2 bg-primary-blue/10 text-primary-blue rounded-lg text-sm font-medium"
                        >
                            Manage Lead
                        </button>
                    </GlassmorphicCard>
                ))}
            </div>

            {/* Lead Management Modal */}
            {selectedLead && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedLead(null)}>
                    <GlassmorphicCard className="w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h3 className="text-xl font-bold text-theme-primary">{selectedLead.studentName}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${selectedLead.type === 'Abroad' ? 'bg-indigo-100 text-indigo-700' : 'bg-orange-100 text-orange-700'}`}>
                                        {selectedLead.type} Lead
                                    </span>
                                    <span className="text-sm text-theme-secondary">{selectedLead.source}</span>
                                    {isLocked(selectedLead.id) && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                            <Lock className="h-3 w-3" /> Locked
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button onClick={() => setSelectedLead(null)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                                <span className="sr-only">Close</span>
                                <svg className="w-5 h-5 text-theme-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-6">
                                {/* Contact Info */}
                                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl space-y-3">
                                    <h4 className="font-semibold text-theme-primary flex items-center gap-2">
                                        <Users className="h-4 w-4" /> Student Details
                                    </h4>

                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center gap-3">
                                            <Phone className="h-4 w-4 text-theme-muted" />
                                            <span className="text-theme-primary">{selectedLead.phone}</span>
                                            <a href={`tel:${selectedLead.phone}`} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded hover:bg-green-200">Call</a>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Mail className="h-4 w-4 text-theme-muted" />
                                            <span className="text-theme-primary">{selectedLead.email}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Calendar className="h-4 w-4 text-theme-muted" />
                                            <span className="text-theme-primary">Created: {new Date(selectedLead.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Interest Info */}
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-theme-primary flex items-center justify-between">
                                        Interest / Requirement
                                        {!isEditingInterest ? (
                                            <button onClick={() => setIsEditingInterest(true)} className="text-xs text-primary-blue hover:underline">Edit</button>
                                        ) : (
                                            <div className="flex gap-2">
                                                <button onClick={() => setIsEditingInterest(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                                                <button onClick={handleSaveInterest} className="text-xs text-green-600 font-bold hover:text-green-700">Save</button>
                                            </div>
                                        )}
                                    </h4>
                                    <div className={`p-4 border rounded-xl bg-white dark:bg-slate-800 transition-colors ${isEditingInterest ? 'border-primary-blue ring-1 ring-primary-blue/20' : 'border-slate-200 dark:border-slate-700'}`}>
                                        {isEditingInterest ? (
                                            <textarea
                                                value={editedInterest}
                                                onChange={e => setEditedInterest(e.target.value)}
                                                className="w-full bg-transparent outline-none text-theme-primary resize-none min-h-[80px]"
                                                placeholder="Enter student requirements or interests..."
                                            />
                                        ) : (
                                            <p className="text-theme-primary whitespace-pre-wrap">{selectedLead.interest || 'No specific details provided.'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* Status Roadmap */}
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-theme-primary">Lead Progress</h4>
                                    <div className="relative pl-4 space-y-6 before:absolute before:left-[5px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200 dark:before:bg-slate-700">
                                        {['new', 'contacted', 'counselling', 'applied', 'admitted'].map((step) => {
                                            const statuses = ['new', 'contacted', 'counselling', 'applied', 'admitted']
                                            const currentIdx = statuses.indexOf(selectedLead.status)
                                            const stepIdx = statuses.indexOf(step)
                                            const isCompleted = currentIdx >= stepIdx
                                            const isCurrent = currentIdx === stepIdx

                                            return (
                                                <div key={step} className="relative flex items-center gap-3">
                                                    <div className={`absolute left-[-21px] w-[10px] h-[10px] rounded-full ring-2 ring-white dark:ring-slate-900 ${isCompleted ? 'bg-primary-blue' : 'bg-slate-300 dark:bg-slate-600'
                                                        }`} />
                                                    <span className={`text-sm capitalize ${isCurrent ? 'font-bold text-primary-blue' : isCompleted ? 'font-medium text-theme-primary' : 'text-theme-muted'}`}>
                                                        {step === 'admitted' ? 'Admitted / Enrolled ✅' : step}
                                                    </span>
                                                    {step === 'admitted' && isLocked(selectedLead.id) && (
                                                        <Lock className="h-3.5 w-3.5 text-green-600" />
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>

                                    <div className="pt-4">
                                        <label className="text-xs font-semibold text-theme-muted uppercase mb-2 block">Update Status</label>
                                        {isLocked(selectedLead.id) ? (
                                            <div className="space-y-2">
                                                <div className="w-full p-2.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 font-semibold text-sm flex items-center gap-2">
                                                    <Lock className="h-4 w-4" /> Status locked — Admitted / Enrolled
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setUnlockLeadId(selectedLead.id)
                                                        setUnlockPassword('')
                                                        setPasswordError(false)
                                                        setShowUnlockModal(true)
                                                    }}
                                                    className="text-xs text-red-500 hover:text-red-700 hover:underline font-medium"
                                                >
                                                    Unlock with admin password
                                                </button>
                                            </div>
                                        ) : (
                                            <select
                                                value={selectedLead.status}
                                                onChange={e => handleUpdateStatus(selectedLead.id, e.target.value)}
                                                className="w-full p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary"
                                            >
                                                <option value="new">New</option>
                                                <option value="contacted">Contacted</option>
                                                <option value="counselling">Counselling In Progress</option>
                                                <option value="applied">Applied / Enrolling</option>
                                                <option value="admitted">Admitted / Enrolled</option>
                                            </select>
                                        )}
                                    </div>
                                </div>

                                {/* Remarks */}
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-theme-primary flex items-center gap-2">
                                        <MessageSquare className="h-4 w-4" /> Remarks & Notes
                                    </h4>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={leadRemark}
                                            onChange={e => setLeadRemark(e.target.value)}
                                            placeholder="Add a note about this lead..."
                                            className="flex-1 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
                                            onKeyDown={e => e.key === 'Enter' && handleAddRemark()}
                                        />
                                        <button
                                            onClick={handleAddRemark}
                                            className="px-4 py-2 bg-primary-blue text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    <div className="max-h-[300px] overflow-y-auto space-y-3 pr-1">
                                        {localRemarks.map((remark, idx) => (
                                            <div key={idx} className={`p-3 rounded-lg border ${remark.type === 'system'
                                                    ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800'
                                                    : 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/20'
                                                }`}>
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className={`text-xs font-bold ${remark.type === 'system' ? 'text-slate-500' : 'text-amber-700 dark:text-amber-500'
                                                        }`}>
                                                        {remark.type === 'system' ? 'System' : 'Counsellor'}
                                                    </span>
                                                    <span className="text-[10px] text-theme-muted">
                                                        {remark.date.toLocaleDateString()} {remark.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-theme-primary">{remark.text}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>
            )}

            {/* Admitted Confirmation Popup */}
            <AnimatePresence>
                {showAdmittedPopup && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => { setShowAdmittedPopup(false); setPendingAdmitLeadId(null) }}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <GlassmorphicCard className="w-full max-w-md p-8 text-center shadow-2xl">
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-4">
                                    <CheckCircle2 className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-theme-primary mb-2">Confirm Admission</h3>
                                <p className="text-sm text-theme-secondary mb-1">
                                    This lead will be marked as <span className="font-bold text-green-600">Admitted / Enrolled</span>.
                                </p>
                                <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 rounded-lg mt-3 mb-6">
                                    <p className="text-sm text-amber-800 dark:text-amber-400 flex items-start gap-2">
                                        <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
                                        <span><strong>Warning:</strong> Once admitted, you won't be able to change the status again without an admin password.</span>
                                    </p>
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => { setShowAdmittedPopup(false); setPendingAdmitLeadId(null) }}
                                        className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-theme-secondary bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmAdmission}
                                        className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg shadow-green-500/25"
                                    >
                                        Yes, Mark as Admitted
                                    </button>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Unlock Password Modal */}
            <AnimatePresence>
                {showUnlockModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => { setShowUnlockModal(false); setUnlockLeadId(null) }}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <GlassmorphicCard className="w-full max-w-sm p-8 text-center shadow-2xl">
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center mb-4">
                                    <Lock className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-theme-primary mb-2">Status Locked</h3>
                                <p className="text-sm text-theme-secondary mb-5">
                                    This lead is admitted. Enter the admin password to unlock and change the status.
                                </p>
                                <input
                                    type="password"
                                    value={unlockPassword}
                                    onChange={e => { setUnlockPassword(e.target.value); setPasswordError(false) }}
                                    placeholder="Enter admin password"
                                    className={`w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border text-sm text-center font-medium tracking-wider focus:outline-none focus:ring-2 ${passwordError
                                        ? 'border-red-500 focus:ring-red-500/50 text-red-500'
                                        : 'border-slate-200 dark:border-slate-700 focus:ring-primary-blue/50 text-theme-primary'
                                        }`}
                                    onKeyDown={e => e.key === 'Enter' && handleUnlock()}
                                    autoFocus
                                />
                                {passwordError && (
                                    <p className="text-xs text-red-500 mt-2 font-medium">Incorrect password. Try again.</p>
                                )}
                                <div className="flex gap-3 mt-5">
                                    <button
                                        onClick={() => { setShowUnlockModal(false); setUnlockLeadId(null) }}
                                        className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-theme-secondary bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUnlock}
                                        className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 transition-all shadow-lg shadow-red-500/25"
                                    >
                                        Unlock
                                    </button>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    )
}
